import React from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";

import * as url from "helper/UrlHelper";

import JoditEditorField from "../JoditEditorField/JoditEditorField";
import productStatusOptions from "data/Prod/ProductStatus.json";
import CurrencyOptions from "data/Prod/Currency.json";
import { reactSelectStyle } from "Config/Config";
import FileUploadField from "../FileUploadField/FileUploadField";

const SaveProductForm = (props) => {
  const { t } = useTranslation(); //for translation

  const {
    productTypeList = [],
    companyList = [],
    productTypeValue = null,
    companyValue = null,
    productTypeSelectionHandler = () => {},
    companySelectionHandler = () => {},
    setCompanyInput = () => {},
    name = "",
    setname = () => {},
    sku = "",
    setSku = () => {},
    summary = "",
    setsummary = () => {},
    details = "",
    setdetails = () => {},
    price = "",
    setprice = () => {},
    currencyValue = null,
    currencySelectionHandler = () => {},
    taxPercent = "",
    settaxPercent = () => {},
    unit = "",
    setUnit = () => {},
    weight = "",
    setweight = () => {},
    length = "",
    setlength = () => {},
    width = "",
    setwidth = () => {},
    height = "",
    setheight = () => {},
    startDate = "",
    setstartDate = () => {},
    endDate = "",
    setendDate = () => {},
    productStatusValue = null,
    productStatusSelectionHandler = () => {},
    imageUploadHandler = () => {},
    uploadedPreviewImage = "",
    setUploadedPreviewImage = () => {},
    setUploadedImageId = () => {},
    isUploading = false,
    isServiceFeatured = false,
    setIsServiceFeatured = () => {},
    isProductFeatured = false,
    setIsProductFeatured = () => {},
    isSaving = false,
    saveProductInfoHandler = () => {},
    customTypeStyles = {},
    customCompanyStyles = {},
    errors = {},
    setErrors = () => {},
  } = props;

  return (
    <div className="p-3 p-lg-4 border border-gray-300 rounded-10 shadow-sm">
      <form onSubmit={(e) => e.preventDefault()}>
        {/* Product Type and Company */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <label
                htmlFor="name"
                className="d-flex align-items-center gap-1 fs-sm fw-semibold mb-2"
              >
                {t("Medicine Type")}
                <span className="text-danger fw-bold">*</span>
              </label>
              <Select
                isClearable
                placeholder={t("Select a category, e.g., Medicine, Drug")}
                options={productTypeList}
                value={productTypeValue}
                onChange={productTypeSelectionHandler}
                styles={customTypeStyles}
                className={`${errors.type ? "is-invalid" : ""}`}
              />
              {errors.type && (
                <div className="invalid-feedback d-block mt-2">
                  {errors.type}
                </div>
              )}
            </div>
            <div className="col-lg-6">
              <label
                htmlFor="companyStore"
                className="d-flex align-items-center gap-1 fs-sm fw-semibold mb-2"
              >
                <span className="d-block">{t("Laboratory")}</span>
                <span className="text-danger fw-bold">*</span>
              </label>
              <div className="d-flex align-items-center gap-3">
                <Select
                  isClearable
                  className={`w-100 ${errors.company ? "is-invalid" : ""}`}
                  placeholder={t("Search and select the associated Laboratory")}
                  options={companyList}
                  value={companyValue}
                  onChange={companySelectionHandler}
                  onInputChange={(val) => {
                    setCompanyInput(val);
                    if (errors.company) {
                      setErrors((prev) => ({ ...prev, company: "" }));
                    }
                  }}
                  styles={customCompanyStyles}
                />
                <Link
                  to="/admin/companies/info"
                  target="_blank"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <span className="d-block material-symbols-outlined icon-fill">
                    add
                  </span>
                </Link>
              </div>
              {errors.company && (
                <div className="invalid-feedback d-block mt-2">
                  {errors.company}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Product Name and SKU */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <label
                htmlFor="name"
                className="d-flex align-items-center gap-1 fs-sm fw-semibold mb-2"
              >
                <span className="d-block">{t("Medicine Name")}</span>
                <span className="text-danger fw-bold">*</span>
              </label>
              <input
                type="text"
                id="name"
                className={`form-control fs-sm shadow-none ${
                  errors.name ? "is-invalid" : "border-gray-300"
                }`}
                placeholder={t("E.g., Paracetamol")}
                value={name}
                onChange={(e) => {
                  setname(e.target.value);
                  setErrors((prev) => ({ ...prev, name: "" }));
                }}
              />
              {errors.name && (
                <div className="invalid-feedback mt-2">{errors.name}</div>
              )}
            </div>
            <div className="col-lg-6">
              <label
                htmlFor="sku"
                className="d-flex align-items-center gap-1 fs-sm fw-semibold mb-2"
              >
                <span className="d-block">{t("National Code")}</span>
                <span className="text-danger fw-bold">*</span>
              </label>
              <input
                type="text"
                id="sku"
                className={`form-control fs-sm shadow-none ${
                  errors.sku ? "is-invalid" : "border-gray-300"
                }`}
                placeholder={t("Enter the national code for the medicine")}
                value={sku}
                onChange={(e) => {
                  setSku(e.target.value);
                  setErrors((prev) => ({ ...prev, sku: "" }));
                }}
              />
              {errors.sku && (
                <div className="invalid-feedback mt-2">{errors.sku}</div>
              )}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <label
            htmlFor="summary"
            className="d-flex align-items-center gap-1 fs-sm fw-semibold mb-2"
          >
            <span className="d-block">{t("Summary")}</span>
          </label>
          <textarea
            name="summary"
            id="summary"
            cols="30"
            rows="5"
            className="form-control fs-sm shadow-none border-gray-300"
            placeholder={t(
              "Write a short, catchy summary of the medicine's main benefits."
            )}
            value={summary}
            onChange={(e) => setsummary(e.target.value)}
          ></textarea>
        </div>
        {/* Details */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <JoditEditorField
            label="Details"
            labelClassName="d-block fs-sm fw-semibold mb-2"
            value={details}
            onChange={(newContent) => {
              setdetails(newContent);
            }}
            editorHeight={250}
          />
          {/* <label
            htmlFor="details"
            className="d-flex align-items-center gap-1 fs-sm fw-semibold mb-2"
          >
            <span className="d-block">{t("Details")}</span>
          </label>
          <JoditEditor
            ref={editor}
            value={details}
            config={editorConfig}
            onBlur={(newContent) => {
              setdetails(newContent);
            }}
          /> */}
        </div>
        {/* Price, Currency, VAT, Stock */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg mb-4 mb-lg-0">
              <label htmlFor="price" className="d-block fs-sm fw-semibold mb-2">
                {t("Price")}
                <span className="text-danger fw-bold">*</span>
              </label>
              <input
                type="number"
                min={0}
                id="price"
                className={`form-control fs-sm shadow-none ${
                  errors.price ? "is-invalid" : "border-gray-300"
                }`}
                placeholder={t("E.g., 99.99")}
                value={price}
                onChange={(e) => {
                  setprice(e.target.value);
                  setErrors((prev) => ({ ...prev, price: "" }));
                }}
              />
              {errors.price && (
                <div className="invalid-feedback mt-2">{errors.price}</div>
              )}
            </div>
            <div className="col-lg mb-4 mb-lg-0">
              <label
                htmlFor="currency"
                className="d-block fs-sm fw-semibold mb-2"
              >
                {t("Currency")}
              </label>
              <Select
                isClearable
                placeholder={t("Select currency")}
                options={CurrencyOptions}
                value={currencyValue}
                onChange={currencySelectionHandler}
                styles={reactSelectStyle("3px", "6px", true, false)}
              />
            </div>
            <div className="col-lg mb-4 mb-lg-0">
              <label htmlFor="vat" className="d-block fs-sm fw-semibold mb-2">
                {t("VAT %")}
                <span className="text-danger fw-bold">*</span>
              </label>
              <input
                type="number"
                min={0}
                id="vat"
                className={`form-control fs-sm shadow-none ${
                  errors.vat ? "is-invalid" : "border-gray-300"
                }`}
                placeholder={t("E.g., 20")}
                value={taxPercent}
                onChange={(e) => {
                  settaxPercent(e.target.value);
                  setErrors((prev) => ({ ...prev, vat: "" }));
                }}
              />
              {errors.vat && (
                <div className="invalid-feedback mt-2">{errors.vat}</div>
              )}
            </div>
            <div className="col-lg mb-4 mb-lg-0">
              <label htmlFor="stock" className="d-block fs-sm fw-semibold mb-2">
                {t("Stock")}
                <span className="text-danger fw-bold">*</span>
              </label>
              <input
                min={0}
                type="number"
                id="stock"
                className={`form-control fs-sm shadow-none ${
                  errors.unit ? "is-invalid" : "border-gray-300"
                }`}
                placeholder={t("E.g., 100")}
                value={unit}
                onChange={(e) => {
                  setUnit(e.target.value);
                  setErrors((prev) => ({ ...prev, unit: "" }));
                }}
              />
              {errors.unit && (
                <div className="invalid-feedback mt-2">{errors.unit}</div>
              )}
            </div>
          </div>
        </div>
        {/* Weight, Length, Width, Height */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg mb-4 mb-lg-0">
              <label
                htmlFor="weight"
                className="d-block fs-sm fw-semibold mb-2"
              >
                {t("Weight (kg)")}
              </label>
              <input
                min={0}
                type="number"
                id="weight"
                className="form-control fs-sm shadow-none border-gray-300"
                placeholder={t("E.g., 0.5")}
                value={weight}
                onChange={(e) => setweight(e.target.value)}
              />
            </div>
            <div className="col-lg mb-4 mb-lg-0">
              <label
                htmlFor="length"
                className="d-block fs-sm fw-semibold mb-2"
              >
                {t("Length (cm)")}
              </label>
              <input
                min={0}
                type="number"
                id="length"
                className="form-control fs-sm shadow-none border-gray-300"
                placeholder={t("E.g., 10")}
                value={length}
                onChange={(e) => setlength(e.target.value)}
              />
            </div>
            <div className="col-lg mb-4 mb-lg-0">
              <label htmlFor="width" className="d-block fs-sm fw-semibold mb-2">
                {t("Width (cm)")}
              </label>
              <input
                min={0}
                type="number"
                id="width"
                className="form-control fs-sm shadow-none border-gray-300"
                placeholder={t("E.g., 5")}
                value={width}
                onChange={(e) => setwidth(e.target.value)}
              />
            </div>
            <div className="col-lg mb-4 mb-lg-0">
              <label
                htmlFor="height"
                className="d-block fs-sm fw-semibold mb-2"
              >
                {t("Height (cm)")}
              </label>
              <input
                min={0}
                type="number"
                id="height"
                className="form-control fs-sm shadow-none border-gray-300"
                placeholder={t("E.g., 2")}
                value={height}
                onChange={(e) => setheight(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* Advanced Information Accordion */}
        <div className="accordion mb-4" id="accordionPanelsStayOpenExample">
          <div className="accordion-item bg-transparent border-0">
            <h3 className="accordion-header">
              <button
                className="accordion-button bg-transparent p-0 shadow-none"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#panelsStayOpen-collapseOne"
                aria-expanded="true"
                aria-controls="panelsStayOpen-collapseOne"
              >
                {t("Advanced Information")}
              </button>
            </h3>
            <div
              id="panelsStayOpen-collapseOne"
              className="accordion-collapse collapse show"
            >
              <div className="accordion-body p-0 pt-4">
                {/* Display Dates and Product Status */}
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <div className="row">
                    <div className="col-lg-4 mb-4 mb-lg-0">
                      <label
                        htmlFor="displayStartFrom"
                        className="d-block fs-sm fw-semibold mb-2"
                      >
                        {t("Manufacturing Date")}
                      </label>
                      <input
                        type="date"
                        className={`form-control fs-sm shadow-none ${
                          errors.dateRange ? "is-invalid" : "border-gray-300"
                        }`}
                        value={startDate}
                        onChange={(e) => {
                          setstartDate(e.target.value);
                          setErrors((prev) => ({
                            ...prev,
                            dateRange: "",
                          }));
                        }}
                      />
                    </div>
                    <div className="col-lg-4 mb-4 mb-lg-0">
                      <label
                        htmlFor="displayEndFrom"
                        className="d-block fs-sm fw-semibold mb-2"
                      >
                        {t("Expiry Date")}
                      </label>
                      <input
                        type="date"
                        className={`form-control fs-sm shadow-none ${
                          errors.dateRange ? "is-invalid" : "border-gray-300"
                        }`}
                        value={endDate}
                        onChange={(e) => {
                          setendDate(e.target.value);
                          setErrors((prev) => ({
                            ...prev,
                            dateRange: "",
                          }));
                        }}
                      />
                      {errors.dateRange && (
                        <div className="invalid-feedback d-block mt-2">
                          {errors.dateRange}
                        </div>
                      )}
                    </div>
                    <div className="col-lg-4">
                      <label
                        htmlFor="displayEndFrom"
                        className="d-block fs-sm fw-semibold mb-2"
                      >
                        {t("Medicine Status")}
                      </label>
                      <Select
                        isClearable
                        placeholder={t(
                          "Set the medicine status, e.g., Active, Draft"
                        )}
                        options={productStatusOptions}
                        value={productStatusValue}
                        onChange={productStatusSelectionHandler}
                        styles={reactSelectStyle("3px", "6px", true, false)}
                      />
                    </div>
                  </div>
                </div>

                {/* Image Upload */}
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label
                    htmlFor="productImage"
                    className="d-block fs-sm fw-semibold mb-2"
                  >
                    {t("Medicine Image")}
                  </label>
                  <FileUploadField
                    label={t("Image")}
                    accept="jpg png jpeg"
                    onUpload={imageUploadHandler}
                    files={
                      uploadedPreviewImage === ""
                        ? [
                            {
                              path: "https://placehold.co/150x150",
                              name: "Medicine Image",
                            },
                          ]
                        : [
                            {
                              path: url.SERVER_URL + uploadedPreviewImage,
                              name: "Medicine Image",
                            },
                          ]
                    }
                    onRemove={() => {
                      setUploadedPreviewImage("");
                      setUploadedImageId(null);
                    }}
                    loading={isUploading}
                    fileType="image"
                  />
                </div>
                {/* Privacy Settings */}
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <p className="mb-3 fs-md fw-semibold">
                    {t("Mark as Service")}
                  </p>
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      checked={isServiceFeatured}
                      onChange={(e) => {
                        setIsServiceFeatured(e.target.checked);
                      }}
                      id="privateEvent"
                    />
                    <label className="form-check-label" htmlFor="privateEvent">
                      {t("Add this item as Service")}
                    </label>
                  </div>

                  <p className="mb-3 fs-md fw-semibold">
                    {t("Mark as Featured Item")}
                  </p>
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      checked={isProductFeatured}
                      onChange={(e) => {
                        setIsProductFeatured(e.target.checked);
                      }}
                      id="privateEvent"
                    />
                    <label className="form-check-label" htmlFor="privateEvent">
                      {t("Add this as featured item")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action d-flex flex-wrap gap-3 align-items-center justify-content-between border-top pt-4 mt-4">
          <Link
            to="/admin/clinicalmanagement/medicines"
            className="btn btn-outline-primary px-4"
          >
            {t("Cancel")}
          </Link>
          <button
            type="submit"
            className="btn btn-primary px-4 d-flex align-items-center"
            disabled={isSaving}
            onClick={saveProductInfoHandler}
          >
            {isSaving ? t("Saving...") : t("Save Medicine")}
            {isSaving && (
              <div
                className="ms-2 spinner-border spinner-border-sm"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SaveProductForm;
